/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import {
  FlexRow,
  type RenderedDimensions,
} from '@lib/canvasx/components/FlexRow';
import { normalizeSpacing } from '@lib/canvasx/shapes';
import type ObjectValue from './objects/ObjectValue';
import type { FieldObjectType } from './objects/ObjectValue';
import FieldObjectValue from './objects/FieldObjectValue';
import ReactionAction from './objects/ReactionAction';
import type WhiteboardController from './controllers/WhiteboardController';
import OpenIntegrationLinkAction from './objects/OpenIntegrationLinkAction';

export interface FieldOptions {
  maxHeight: number;
  scale: number;
  rect: Partial<ShapeStruct>;
}

function calculateChildWidth(
  width: number,
  childCount: number,
  gap: SpacingAxisStruct
) {
  return (width - gap.horizontal * (childCount - 1)) / childCount;
}

const OBJECT_VALUES: Array<typeof ObjectValue> = [
  // All ObjectValue classes that render items on cards
  OpenIntegrationLinkAction,
  FieldObjectValue,
  ReactionAction,
];

export function shouldRenderFields(
  object: FieldObjectType,
  controller: WhiteboardController
): boolean {
  return OBJECT_VALUES.some(ObjectValue =>
    ObjectValue.shouldRender(object, controller)
  );
}

/**
 * Renders the custom fields of the object into the defined rect, limiting the
 * fields to `maxHeight` height of content, and scaling the fields by `scale`. Use
 * `scale` if the default size/scale of the object is smaller/bigger.
 */
export function renderFields(
  ctx: CanvasRenderingContext2D,
  object: FieldObjectType,
  controller: WhiteboardController,
  options: Partial<FieldOptions> = {}
): RenderedDimensions {
  const { maxHeight = 25, scale = 1, rect = {} } = options;

  const { scaleX = 1, scaleY = 1 } = object;

  const {
    x = -object.width / 2,
    y = -object.height / 2,
    width = (object.width * scaleX) / scale,
    height = (object.height * scaleY) / scale,
    padding = 3,
  } = rect;

  const normalizedPadding = normalizeSpacing(padding);

  const rowHeight = 10;
  let renderedDimensions: RenderedDimensions = {
    width,
    height: rowHeight,
    rows: 1,
  };

  const gap: SpacingAxisStruct = { horizontal: 3, vertical: 4 };
  let childCount = 2;
  const contentWidth = width - normalizedPadding.left - normalizedPadding.right;
  while (calculateChildWidth(contentWidth, childCount, gap) > 80) {
    childCount++;
  }

  const maxChildWidth = calculateChildWidth(contentWidth, childCount, gap);

  // Offset position of fields
  ctx.save();
  ctx.translate(x, y);

  withContext({ canvas: object.canvas, ctx, object, scale }, () => {
    ctx.scale(1 / scaleX, 1 / scaleY);

    renderElement(
      <FlexRow
        gap={gap}
        width={width}
        height={height}
        padding={normalizedPadding}
        alignBottom
        maxHeight={Math.min(maxHeight, height * scaleY, 1)}
        maxRows={2}
        verticalAlignment='center'
        setRenderedDimensions={dimensions => {
          renderedDimensions = dimensions;
        }}
      >
        {OBJECT_VALUES.flatMap(ObjectValue =>
          ObjectValue.renderFields(maxChildWidth, object, controller)
        ).filter(Boolean)}
      </FlexRow>
    );
  });

  ctx.restore();

  return renderedDimensions;
}
